
import { useState, useEffect } from 'react';
import { getAllUsersFromStorage } from '../../../utils/sharedStorage';

interface Promoteur {
  phone: string;
  name: string;
  country: string;
  referralCode: string;
  totalFilleuls: number;
  filleulsActifs: number;
  filleulsEnAttente: number;
  balance: number;
  registrationDate: string;
  accountStatus: string;
  level1: number;
  level2: number;
  level3: number;
}

export default function Promoteurs() {
  const [promoteurs, setPromoteurs] = useState<Promoteur[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'total' | 'actifs' | 'balance'>('total');
  const [selectedPromoteur, setSelectedPromoteur] = useState<Promoteur | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filleulsDetails, setFilleulsDetails] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadPromoteurs();
    const interval = setInterval(() => loadPromoteurs(), 30000);
    return () => clearInterval(interval);
  }, []);

  const loadPromoteurs = () => {
    setIsLoading(true);
    try {
      let allUsers: any[] = [];
      try {
        allUsers = getAllUsersFromStorage();
        if (!Array.isArray(allUsers)) allUsers = [];
      } catch (e) {
        console.error('Erreur getAllUsersFromStorage:', e);
        allUsers = [];
      }

      // Construire la liste des promoteurs (ceux qui ont au moins 1 filleul)
      const promoMap: Record<string, Promoteur> = {};

      allUsers.forEach(user => {
        try {
          if (!user || !user.referralCode) return;

          // Compter les filleuls directs (niveau 1)
          const filleulsNiv1 = allUsers.filter(u => {
            try { return u.referredBy === user.referralCode || u.referredBy === user.phone; }
            catch { return false; }
          });
          const filleulsNiv1Actifs = filleulsNiv1.filter(u => {
            try { return u.accountStatus === 'active'; } catch { return false; }
          });
          const filleulsNiv1Attente = filleulsNiv1.filter(u => {
            try { return u.accountStatus !== 'active'; } catch { return false; }
          });

          // Niveau 2 : filleuls des filleuls
          const filleulsNiv2: any[] = [];
          filleulsNiv1.forEach(f1 => {
            try {
              const niv2 = allUsers.filter(u => {
                try { return u.referredBy === f1.referralCode || u.referredBy === f1.phone; }
                catch { return false; }
              });
              filleulsNiv2.push(...niv2);
            } catch { /* ignorer */ }
          });

          // Niveau 3 : filleuls des filleuls des filleuls
          const filleulsNiv3: any[] = [];
          filleulsNiv2.forEach(f2 => {
            try {
              const niv3 = allUsers.filter(u => {
                try { return u.referredBy === f2.referralCode || u.referredBy === f2.phone; }
                catch { return false; }
              });
              filleulsNiv3.push(...niv3);
            } catch { /* ignorer */ }
          });

          const totalFilleuls = filleulsNiv1.length + filleulsNiv2.length + filleulsNiv3.length;

          if (totalFilleuls > 0) {
            promoMap[user.phone] = {
              phone: user.phone || '',
              name: user.name || '',
              country: user.country || '',
              referralCode: user.referralCode || '',
              totalFilleuls,
              filleulsActifs: filleulsNiv1Actifs.length,
              filleulsEnAttente: filleulsNiv1Attente.length,
              balance: typeof user.balance === 'number' ? user.balance : 0,
              registrationDate: user.registrationDate || '',
              accountStatus: user.accountStatus || 'pending',
              level1: filleulsNiv1.length,
              level2: filleulsNiv2.length,
              level3: filleulsNiv3.length,
            };
          }
        } catch (e) {
          console.error('Erreur traitement promoteur:', e);
        }
      });

      const list = Object.values(promoMap);
      list.sort((a, b) => {
        try {
          if (sortBy === 'total') return b.totalFilleuls - a.totalFilleuls;
          if (sortBy === 'actifs') return b.filleulsActifs - a.filleulsActifs;
          return b.balance - a.balance;
        } catch { return 0; }
      });

      setPromoteurs(list);
    } catch (e) {
      console.error('Erreur loadPromoteurs:', e);
      setPromoteurs([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPromoteurs();
  }, [sortBy]);

  const handleViewDetails = (promo: Promoteur) => {
    try {
      setSelectedPromoteur(promo);
      let allUsers: any[] = [];
      try {
        allUsers = getAllUsersFromStorage();
        if (!Array.isArray(allUsers)) allUsers = [];
      } catch (e) {
        console.error('Erreur getAllUsersFromStorage:', e);
        allUsers = [];
      }
      const filleuls = allUsers.filter(u => {
        try { return u.referredBy === promo.referralCode || u.referredBy === promo.phone; }
        catch { return false; }
      });
      setFilleulsDetails(filleuls);
      setShowDetailsModal(true);
    } catch (e) {
      console.error('Erreur handleViewDetails:', e);
    }
  };

  const filtered = promoteurs.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.phone.includes(searchTerm) ||
    p.referralCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalFilleulsGlobal = promoteurs.reduce((s, p) => s + p.level1, 0);
  const totalActifsGlobal = promoteurs.reduce((s, p) => s + p.filleulsActifs, 0);

  const formatDate = (d: string) => {
    if (!d) return '-';
    const date = new Date(d);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getRankBadge = (index: number) => {
    if (index === 0) return { icon: 'ri-medal-line', color: 'text-yellow-500', bg: 'bg-yellow-50', label: '#1' };
    if (index === 1) return { icon: 'ri-medal-line', color: 'text-gray-400', bg: 'bg-gray-50', label: '#2' };
    if (index === 2) return { icon: 'ri-medal-line', color: 'text-orange-400', bg: 'bg-orange-50', label: '#3' };
    return { icon: 'ri-user-star-line', color: 'text-red-500', bg: 'bg-red-50', label: `#${index + 1}` };
  };

  return (
    <div>
      {/* Modal détails promoteur */}
      {showDetailsModal && selectedPromoteur && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
            {/* Header modal */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-t-2xl px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-user-star-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">{selectedPromoteur.name}</h3>
                    <p className="text-red-100 text-sm">@{selectedPromoteur.referralCode} • {selectedPromoteur.phone}</p>
                  </div>
                </div>
                <button onClick={() => setShowDetailsModal(false)} className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full cursor-pointer hover:bg-white/30">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Stats réseau */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-red-50 rounded-xl p-4 text-center border border-red-100">
                  <p className="text-2xl font-bold text-red-600">{selectedPromoteur.level1}</p>
                  <p className="text-xs text-gray-600 mt-1">Niveau 1</p>
                  <p className="text-xs text-gray-400">Filleuls directs</p>
                </div>
                <div className="bg-orange-50 rounded-xl p-4 text-center border border-orange-100">
                  <p className="text-2xl font-bold text-orange-500">{selectedPromoteur.level2}</p>
                  <p className="text-xs text-gray-600 mt-1">Niveau 2</p>
                  <p className="text-xs text-gray-400">Filleuls indirects</p>
                </div>
                <div className="bg-yellow-50 rounded-xl p-4 text-center border border-yellow-100">
                  <p className="text-2xl font-bold text-yellow-600">{selectedPromoteur.level3}</p>
                  <p className="text-xs text-gray-600 mt-1">Niveau 3</p>
                  <p className="text-xs text-gray-400">Réseau étendu</p>
                </div>
              </div>

              {/* Infos */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500">Pays</p>
                  <p className="text-sm font-semibold text-gray-900 mt-0.5">{selectedPromoteur.country || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500">Solde</p>
                  <p className="text-sm font-bold text-green-600 mt-0.5">{selectedPromoteur.balance} F</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500">Inscription</p>
                  <p className="text-sm font-semibold text-gray-900 mt-0.5">{formatDate(selectedPromoteur.registrationDate)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500">Statut</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold mt-0.5 ${
                    selectedPromoteur.accountStatus === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {selectedPromoteur.accountStatus === 'active' ? 'Actif' : 'En attente'}
                  </span>
                </div>
              </div>

              {/* Liste filleuls directs */}
              <div>
                <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <i className="ri-group-line text-red-500"></i>
                  Filleuls directs ({filleulsDetails.length})
                </h4>
                {filleulsDetails.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">Aucun filleul direct trouvé</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {filleulsDetails.map(f => (
                      <div key={f.phone} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-red-100 rounded-full flex items-center justify-center">
                            <i className="ri-user-line text-red-500 text-sm"></i>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{f.name || f.phone}</p>
                            <p className="text-xs text-gray-500">{f.phone}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                          f.accountStatus === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {f.accountStatus === 'active' ? 'Actif' : 'En attente'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowDetailsModal(false)}
                className="w-full mt-5 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm cursor-pointer whitespace-nowrap transition"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* En-tête */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Les Promoteurs</h2>
            <p className="text-sm text-gray-500 mt-0.5">Membres actifs qui recrutent et développent le réseau</p>
          </div>
          <button
            onClick={loadPromoteurs}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 cursor-pointer whitespace-nowrap text-sm font-medium disabled:opacity-50 transition"
          >
            <i className={`ri-refresh-line ${isLoading ? 'animate-spin' : ''}`}></i>
            Actualiser
          </button>
        </div>

        {/* Statistiques globales */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total promoteurs</p>
                <p className="text-3xl font-bold mt-1">{promoteurs.length}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-user-star-line text-2xl"></i>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total filleuls (niv.1)</p>
                <p className="text-3xl font-bold mt-1">{totalFilleulsGlobal}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-group-line text-2xl"></i>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Filleuls actifs</p>
                <p className="text-3xl font-bold mt-1">{totalActifsGlobal}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-check-double-line text-2xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              placeholder="Rechercher un promoteur..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 font-medium whitespace-nowrap">Trier par :</span>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as any)}
              className="px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm cursor-pointer"
            >
              <option value="total">Total filleuls</option>
              <option value="actifs">Filleuls actifs</option>
              <option value="balance">Solde</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Rang</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Promoteur</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Code invitation</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Pays</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Niv. 1</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Niv. 2</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Niv. 3</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Total réseau</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Solde</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((promo, index) => {
                const rank = getRankBadge(index);
                return (
                  <tr key={promo.phone} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-4">
                      <div className={`w-9 h-9 flex items-center justify-center rounded-full ${rank.bg}`}>
                        <span className={`text-sm font-bold ${rank.color}`}>{rank.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <p className="text-sm font-bold text-gray-900">{promo.name || '-'}</p>
                        <p className="text-xs text-gray-500">{promo.phone}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm font-semibold text-red-600">@{promo.referralCode}</span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">{promo.country || '-'}</td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-700 text-sm font-bold">
                        {promo.level1}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-700 text-sm font-bold">
                        {promo.level2}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100 text-yellow-700 text-sm font-bold">
                        {promo.level3}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-gray-900 text-white text-sm font-bold">
                        {promo.totalFilleuls}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm font-bold text-green-600">{promo.balance} F</td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                        promo.accountStatus === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {promo.accountStatus === 'active' ? 'Actif' : 'En attente'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => handleViewDetails(promo)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg cursor-pointer transition"
                        title="Voir les filleuls"
                      >
                        <i className="ri-eye-line text-base"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filtered.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3">
                <i className="ri-user-star-line text-5xl"></i>
              </div>
              <p className="text-lg font-semibold">Aucun promoteur trouvé</p>
              <p className="text-sm mt-1">Les membres qui recrutent apparaîtront ici automatiquement</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
